import Papa from "papaparse"
import { CustomRateType, iCurrentUser, RateType } from "../../common/types"
import { tEntitySelection } from "../../cached-data/types"
import { toSnakeCase } from "../../common/ts-utils"

/** Separator used for multi-value fields within a single CSV cell. */
const MULTI_VALUE_SEPARATOR = "; "

/**
 * Discriminated union describing how a single CSV column maps to rate object data.
 * Each variant defines a different data access pattern for export/import.
 */
type CsvColumnKind =
    | { kind: "string"; field: string }
    | { kind: "number"; field: string }
    | { kind: "multiString"; field: string; objectKey?: string }
    | {
          kind: "idArray"
          field: string
          resourceName: string
          displayKey: string
          picklistName?: string
      }
    | { kind: "customField"; modelKey: string; fieldName: string }
    | { kind: "customRate"; rateName: string }

/**
 * Represents a single column in the rate table CSV.
 * Combines a header name, rate-vs-identity flag, and the data access pattern.
 */
type CsvColumn = {
    /** CSV column header name */
    header: string
    /** If true, this is a rate value column excluded from the identity hash */
    isRate: boolean
} & CsvColumnKind

/**
 * Result of parsing a CSV file into rate row objects.
 */
type ParseResult = {
    /** Parsed rate row objects ready for merge */
    rows: Record<string, unknown>[]
    /** Human-readable warnings (e.g., unmatched display names) */
    warnings: string[]
}

// ---------------------------------------------------------------------------
// Helpers: currency conversion
// ---------------------------------------------------------------------------

/**
 * Convert a stored currency value (e.g., cents) to a formatted display string (e.g., "10.00").
 * The number of decimal places is derived from the multiplier (100 → 2, 1000 → 3, 1 → 0).
 * @param value - The stored value in smallest currency units.
 * @param currencyMultiplier - The multiplier (e.g., 100 for USD).
 * @returns The formatted display value with appropriate decimal places.
 */
function currencyStoredToDisplay(value: number, currencyMultiplier: number): string {
    const decimalPlaces = Math.log10(currencyMultiplier)
    return (value / currencyMultiplier).toFixed(decimalPlaces)
}

/**
 * Convert a display currency value (e.g., dollars) to a stored value (e.g., cents).
 * Rounds to avoid floating point precision issues (e.g., 10.56 * 100 = 1055.9999...).
 * This matches the pattern used in currencyValueParser for AG-Grid.
 * @param value - The display value in user-facing currency units.
 * @param currencyMultiplier - The multiplier (e.g., 100 for USD).
 * @returns The stored value in smallest currency units.
 */
function currencyDisplayToStored(value: number, currencyMultiplier: number): number {
    return currencyMultiplier !== 1 ? Math.round(value * currencyMultiplier) : value
}

// ---------------------------------------------------------------------------
// Helpers: ID ↔ display name resolution
// ---------------------------------------------------------------------------

/**
 * Resolve an array of numeric IDs to display name strings using referenceableData.
 * @param ids - The IDs to resolve.
 * @param referenceableData - The referenceable data store.
 * @param resourceName - Key into referenceableData (e.g., "cohorts", "picklistItems").
 * @param displayKey - Property on the entity to use as display name (e.g., "name").
 * @returns Resolved display names (unresolvable IDs are omitted).
 */
function resolveIdsToDisplayNames(ids: number[] | undefined, referenceableData: tEntitySelection, resourceName: string, displayKey: string): string[] {
    if (!ids || !Array.isArray(ids)) return []
    const entities = referenceableData[resourceName as keyof tEntitySelection] as Record<number, Record<string, unknown>> | undefined
    if (!entities) return []
    return ids
        .map(id => {
            const entity = entities[id]
            if (!entity) return undefined
            const value = entity[displayKey]
            return value != null ? String(value) : undefined
        })
        .filter((v): v is string => v !== undefined)
}

/**
 * Resolve a display name string back to a numeric ID by searching referenceableData.
 * When picklistName is provided, only entities whose "picklist_name" field matches
 * are considered, preventing cross-picklist name collisions.
 * @param displayName - The display name to look up.
 * @param referenceableData - The referenceable data store.
 * @param resourceName - Key into referenceableData (e.g., "cohorts", "picklistItems").
 * @param displayKey - Property on the entity to match against (e.g., "name").
 * @param picklistName - Optional picklist name to scope the lookup for picklistItems (e.g., "Union").
 * @returns The matching entity ID, or undefined if not found.
 */
function resolveDisplayNameToId(displayName: string, referenceableData: tEntitySelection, resourceName: string, displayKey: string, picklistName?: string): number | undefined {
    const entities = referenceableData[resourceName as keyof tEntitySelection] as Record<number, Record<string, unknown>> | undefined
    if (!entities) return undefined

    for (const entity of Object.values(entities)) {
        // When a picklistName is specified, only match entities belonging to that picklist.
        // entity.picklist_name holds the parent picklist's name (e.g., "Union", "License").
        if (picklistName && entity.picklist_name !== picklistName) continue
        if (String(entity[displayKey]) === displayName) {
            return entity.id as number
        }
    }
    return undefined
}

/**
 * Split a semicolon-separated CSV cell value into trimmed string values.
 * @param value - The CSV cell value.
 * @returns Array of trimmed values, empty strings filtered out.
 */
function splitMultiValue(value: string): string[] {
    if (!value || !value.trim()) return []
    return value
        .split(";")
        .map(v => v.trim())
        .filter(Boolean)
}

/**
 * Join an array of values into a semicolon-separated CSV cell string.
 * @param values - The values to join.
 * @returns Semicolon-separated string.
 */
function joinMultiValue(values: string[]): string {
    return values.join(MULTI_VALUE_SEPARATOR)
}

/**
 * Extract string values from an array that may contain plain strings or keyed objects.
 * Handles the mixed formats found in rate table local state (e.g., trades may be
 * strings or {name: "..."} objects depending on whether they've been normalized).
 * @param values - Array of strings or objects.
 * @param objectKey - Property name to extract from objects (default: "name").
 * @returns Extracted string values.
 */
function extractStringValues(values: unknown[] | undefined, objectKey: string = "name"): string[] {
    if (!values || !Array.isArray(values)) return []
    return values
        .map(v => {
            if (typeof v === "string") return v
            if (v && typeof v === "object" && objectKey in v) {
                return String((v as Record<string, unknown>)[objectKey])
            }
            return undefined
        })
        .filter((v): v is string => v !== undefined)
}

// ---------------------------------------------------------------------------
// Column builders per rate type
// ---------------------------------------------------------------------------

/**
 * Build CSV column definitions for labor rates.
 * @param customRateTypes - Custom rate type definitions from the rate table.
 * @param currentUser - Current user for feature flags and text field options.
 * @returns Column definitions for labor rate CSV.
 */
function getLaborRateColumns(customRateTypes: CustomRateType[], currentUser: iCurrentUser): CsvColumn[] {
    const columns: CsvColumn[] = [
        { header: "Name", field: "name", kind: "string", isRate: false },
        { header: "Time Type", field: "time_type", kind: "string", isRate: false },
        {
            header: "Cohorts",
            field: "cohorts",
            kind: "idArray",
            resourceName: "cohorts",
            displayKey: "name",
            isRate: false,
        },
        {
            header: "Unions",
            field: "unions",
            kind: "idArray",
            resourceName: "picklistItems",
            displayKey: "name",
            picklistName: "Union",
            isRate: false,
        },
        {
            header: "Licenses",
            field: "licenses",
            kind: "idArray",
            resourceName: "picklistItems",
            displayKey: "name",
            picklistName: "License",
            isRate: false,
        },
        {
            header: "Certifications",
            field: "certifications",
            kind: "idArray",
            resourceName: "picklistItems",
            displayKey: "name",
            picklistName: "Certification",
            isRate: false,
        },
        { header: "Trades", field: "trades", kind: "multiString", objectKey: "name", isRate: false },
        {
            header: "Classifications",
            field: "classifications",
            kind: "multiString",
            objectKey: "name",
            isRate: false,
        },
        {
            header: "Client Names",
            field: "project_client_names",
            kind: "multiString",
            objectKey: "name",
            isRate: false,
        },
    ]

    // Add modifier columns if cost_code_level_modifiers feature is enabled
    if (currentUser.features.cost_code_level_modifiers) {
        const tkModifiers = currentUser.company_tk_modifiers ?? []

        // Equipment modifier (standard — trade and classification are handled by their own columns)
        columns.push({
            header: "Equipment Modifier",
            field: "equipment_modifiers",
            kind: "idArray",
            resourceName: "equipment",
            displayKey: "equipment_id",
            isRate: false,
        })

        // Custom modifiers (each backed by a picklist)
        tkModifiers
            .filter(mod => mod.is_active && !["trade", "classification", "equipment"].includes(mod.slug))
            .forEach(mod => {
                columns.push({
                    header: mod.name,
                    field: `custom_modifier_${mod.public_api_field_name}`,
                    kind: "idArray",
                    resourceName: "picklistItems",
                    displayKey: "name",
                    picklistName: mod.name,
                    isRate: false,
                })
            })
    }

    // Custom field columns (Employee, Cost Code, Project)
    currentUser.text_field_options
        .filter(opt => ["Employee", "Cost Code", "Project"].includes(opt.model))
        .forEach(opt => {
            columns.push({
                header: opt.label,
                kind: "customField",
                modelKey: toSnakeCase(opt.model),
                fieldName: opt.name,
                isRate: false,
            })
        })

    // Rate columns
    columns.push({ header: "Base Rate", field: "base_rate", kind: "number", isRate: true })

    customRateTypes
        .filter(crt => crt.show_on_labor_rate)
        .forEach(crt => {
            columns.push({
                header: crt.label,
                kind: "customRate",
                rateName: crt.name,
                isRate: true,
            })
        })

    return columns
}

/**
 * Build CSV column definitions for equipment rates.
 * @param customRateTypes - Custom rate type definitions from the rate table.
 * @param currentUser - Current user for text field options.
 * @returns Column definitions for equipment rate CSV.
 */
function getEquipmentRateColumns(customRateTypes: CustomRateType[], currentUser: iCurrentUser): CsvColumn[] {
    const columns: CsvColumn[] = [
        { header: "Name", field: "name", kind: "string", isRate: false },
        {
            header: "Equipment",
            field: "equipment",
            kind: "idArray",
            resourceName: "equipment",
            displayKey: "equipment_id",
            isRate: false,
        },
        { header: "Types", field: "categories", kind: "multiString", objectKey: "name", isRate: false },
        { header: "Statuses", field: "statuses", kind: "multiString", isRate: false },
    ]

    // Custom field columns (Equipment, Cost Code, Project)
    currentUser.text_field_options
        .filter(opt => ["Equipment", "Cost Code", "Project"].includes(opt.model))
        .forEach(opt => {
            columns.push({
                header: opt.label,
                kind: "customField",
                modelKey: toSnakeCase(opt.model),
                fieldName: opt.name,
                isRate: false,
            })
        })

    // Rate columns
    columns.push(
        { header: "Running Rate", field: "base_rate", kind: "number", isRate: true },
        { header: "Overtime Rate", field: "overtime_rate", kind: "number", isRate: true },
        { header: "Idle Rate", field: "idle_rate", kind: "number", isRate: true }
    )

    customRateTypes
        .filter(crt => crt.show_on_equipment_rate)
        .forEach(crt => {
            columns.push({
                header: crt.label,
                kind: "customRate",
                rateName: crt.name,
                isRate: true,
            })
        })

    return columns
}

/**
 * Build CSV column definitions for material rates.
 * @param customRateTypes - Custom rate type definitions from the rate table.
 * @param currentUser - Current user for text field options.
 * @returns Column definitions for material rate CSV.
 */
function getMaterialRateColumns(customRateTypes: CustomRateType[], currentUser: iCurrentUser): CsvColumn[] {
    const columns: CsvColumn[] = [
        { header: "Name", field: "name", kind: "string", isRate: false },
        {
            header: "Materials",
            field: "materials",
            kind: "idArray",
            resourceName: "materials",
            displayKey: "name",
            isRate: false,
        },
        {
            header: "Material Groups",
            field: "groups",
            kind: "multiString",
            objectKey: "group",
            isRate: false,
        },
        {
            header: "Part Numbers",
            field: "part_numbers",
            kind: "multiString",
            objectKey: "part_number",
            isRate: false,
        },
    ]

    // Custom field columns (Material, Cost Code, Project)
    currentUser.text_field_options
        .filter(opt => ["Material", "Cost Code", "Project"].includes(opt.model))
        .forEach(opt => {
            columns.push({
                header: opt.label,
                kind: "customField",
                modelKey: toSnakeCase(opt.model),
                fieldName: opt.name,
                isRate: false,
            })
        })

    // Rate columns
    columns.push({ header: "Unit Price", field: "base_rate", kind: "number", isRate: true })

    customRateTypes
        .filter(crt => crt.show_on_material_rate)
        .forEach(crt => {
            columns.push({
                header: crt.label,
                kind: "customRate",
                rateName: crt.name,
                isRate: true,
            })
        })

    return columns
}

/**
 * Get CSV column definitions for the given rate type.
 * @param rateType - Which rate type to get columns for.
 * @param customRateTypes - Custom rate type definitions.
 * @param currentUser - Current user context.
 * @returns Column definitions for the rate type.
 */
export function getColumnsForRateType(rateType: RateType, customRateTypes: CustomRateType[], currentUser: iCurrentUser): CsvColumn[] {
    switch (rateType) {
        case "laborRates":
            return getLaborRateColumns(customRateTypes, currentUser)
        case "equipmentRates":
            return getEquipmentRateColumns(customRateTypes, currentUser)
        case "materialRates":
            return getMaterialRateColumns(customRateTypes, currentUser)
    }
}

// ---------------------------------------------------------------------------
// Export: rate row → CSV cell value
// ---------------------------------------------------------------------------

/**
 * Extract a CSV cell value from a rate row for the given column definition.
 * Currency values (columns with isRate=true) are converted from stored units
 * to display units (e.g., cents → dollars) using the provided multiplier.
 * @param row - The rate row object.
 * @param col - The column definition.
 * @param referenceableData - For resolving IDs to display names.
 * @param currencyMultiplier - Divisor for converting stored values to display values (e.g., 100 for USD).
 * @returns The formatted CSV cell value.
 */
function getExportValue(row: Record<string, unknown>, col: CsvColumn, referenceableData: tEntitySelection, currencyMultiplier: number = 100): string {
    switch (col.kind) {
        case "string": {
            const val = row[col.field]
            return val != null ? String(val) : ""
        }
        case "number": {
            const val = row[col.field]
            if (val == null) return ""
            return col.isRate ? currencyStoredToDisplay(val as number, currencyMultiplier) : String(val)
        }
        case "multiString": {
            const values = extractStringValues(row[col.field] as unknown[], col.objectKey)
            return joinMultiValue(values)
        }
        case "idArray": {
            const ids = row[col.field] as number[] | undefined
            const names = resolveIdsToDisplayNames(ids, referenceableData, col.resourceName, col.displayKey)
            return joinMultiValue(names)
        }
        case "customField": {
            const customFields = row.custom_fields as Record<string, Record<string, string[]>> | undefined
            const values = customFields?.[col.modelKey]?.[col.fieldName]
            if (!values || !Array.isArray(values)) return ""
            return joinMultiValue(values)
        }
        case "customRate": {
            const customRates = row.custom_rates as Record<string, number> | undefined
            const val = customRates?.[col.rateName]
            if (val == null) return ""
            return currencyStoredToDisplay(val, currencyMultiplier)
        }
    }
}

// ---------------------------------------------------------------------------
// Import: CSV cell value → rate row data
// ---------------------------------------------------------------------------

/**
 * Set a value on a rate row object from a parsed CSV cell value.
 * Returns a warning string if an ID-based value couldn't be resolved.
 * @param row - The rate row object being built.
 * @param csvValue - The raw CSV cell value.
 * @param col - The column definition.
 * @param referenceableData - For resolving display names to IDs.
 * @param rowIndex - 1-based row number for warning messages.
 * @param currencyMultiplier - Multiplier for converting display values back to stored units (e.g., 100 for USD).
 * @returns A warning message if resolution failed, or undefined.
 */
function setImportValue(row: Record<string, unknown>, csvValue: string, col: CsvColumn, referenceableData: tEntitySelection, rowIndex: number, currencyMultiplier: number = 100): string | undefined {
    const trimmed = csvValue?.trim() ?? ""

    switch (col.kind) {
        case "string": {
            row[col.field] = trimmed || undefined
            return undefined
        }
        case "number": {
            if (trimmed) {
                const parsed = parseFloat(trimmed)
                if (isNaN(parsed)) {
                    row[col.field] = undefined
                } else {
                    row[col.field] = col.isRate ? currencyDisplayToStored(parsed, currencyMultiplier) : parsed
                }
            }
            return undefined
        }
        case "multiString": {
            const values = splitMultiValue(trimmed)
            if (col.objectKey) {
                row[col.field] = values.map(v => ({ [col.objectKey!]: v }))
            } else {
                row[col.field] = values
            }
            return undefined
        }
        case "idArray": {
            const names = splitMultiValue(trimmed)
            if (names.length === 0) {
                row[col.field] = []
                return undefined
            }
            const ids: number[] = []
            const unmatched: string[] = []
            names.forEach(name => {
                const id = resolveDisplayNameToId(name, referenceableData, col.resourceName, col.displayKey, col.picklistName)
                if (id !== undefined) {
                    ids.push(id)
                } else {
                    unmatched.push(name)
                }
            })
            row[col.field] = ids
            if (unmatched.length > 0) {
                return `Row ${rowIndex}: Could not resolve ${col.header} value(s): ${unmatched.join(", ")}`
            }
            return undefined
        }
        case "customField": {
            const values = splitMultiValue(trimmed)
            if (!row.custom_fields) {
                row.custom_fields = {}
            }
            const customFields = row.custom_fields as Record<string, Record<string, string[]>>
            if (!customFields[col.modelKey]) {
                customFields[col.modelKey] = {}
            }
            customFields[col.modelKey][col.fieldName] = values
            return undefined
        }
        case "customRate": {
            if (trimmed) {
                const parsed = parseFloat(trimmed)
                if (!row.custom_rates) {
                    row.custom_rates = {}
                }
                const customRates = row.custom_rates as Record<string, number | null>
                customRates[col.rateName] = isNaN(parsed) ? null : currencyDisplayToStored(parsed, currencyMultiplier)
            }
            return undefined
        }
    }
}

// ---------------------------------------------------------------------------
// Export CSV
// ---------------------------------------------------------------------------

/**
 * Export rate rows to a CSV file and trigger a browser download.
 * @param rows - The rate rows to export.
 * @param rateType - Which rate type is being exported.
 * @param customRateTypes - Custom rate type definitions.
 * @param currentUser - Current user context.
 * @param referenceableData - For resolving IDs to display names.
 * @param filename - The filename for the downloaded CSV.
 * @param currencyMultiplier - Divisor for converting stored values to display values (default: 100 for USD).
 */
export function exportRatesToCsv(
    rows: Record<string, unknown>[],
    rateType: RateType,
    customRateTypes: CustomRateType[],
    currentUser: iCurrentUser,
    referenceableData: tEntitySelection,
    filename: string,
    currencyMultiplier: number = 100
): void {
    const columns = getColumnsForRateType(rateType, customRateTypes, currentUser)

    const csvData = rows.map(row => {
        const csvRow: Record<string, string> = {}
        columns.forEach(col => {
            csvRow[col.header] = getExportValue(row, col, referenceableData, currencyMultiplier)
        })
        return csvRow
    })

    const headers = columns.map(col => col.header)
    const csv = Papa.unparse({ fields: headers, data: csvData })
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

// ---------------------------------------------------------------------------
// Import / Parse CSV
// ---------------------------------------------------------------------------

/**
 * Parse a CSV string into rate row objects for the given rate type.
 * Resolves display names back to IDs where applicable.
 * @param csvText - The raw CSV file content.
 * @param rateType - Which rate type is being imported.
 * @param customRateTypes - Custom rate type definitions.
 * @param currentUser - Current user context.
 * @param referenceableData - For resolving display names to IDs.
 * @param currencyMultiplier - Multiplier for converting display values to stored values (default: 100 for USD).
 * @returns The parsed rows and any warnings about unresolved values.
 */
export function parseCsvToRates(
    csvText: string,
    rateType: RateType,
    customRateTypes: CustomRateType[],
    currentUser: iCurrentUser,
    referenceableData: tEntitySelection,
    currencyMultiplier: number = 100
): ParseResult {
    const columns = getColumnsForRateType(rateType, customRateTypes, currentUser)
    const result = Papa.parse<Record<string, string>>(csvText, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header: string) => header.trim(),
    })

    if (result.errors.length > 0) {
        return {
            rows: [],
            warnings: result.errors.map(e => `CSV parse error at row ${(e.row ?? 0) + 1}: ${e.message}`),
        }
    }

    const warnings: string[] = []

    // Warn about CSV columns that don't match any expected column for this rate type
    const expectedHeaders = new Set(columns.map(col => col.header))
    const csvHeaders = result.meta.fields ?? []
    const unexpectedHeaders = csvHeaders.filter(h => !expectedHeaders.has(h))
    if (unexpectedHeaders.length > 0) {
        warnings.push(
            `CSV contains columns not recognized for this rate type: ${unexpectedHeaders.join(", ")}. ` + "These columns will be ignored. Verify you are uploading the correct CSV for this tab."
        )
    }

    // Warn about expected columns missing from the CSV
    const csvHeaderSet = new Set(csvHeaders)
    const missingHeaders = columns.filter(col => !csvHeaderSet.has(col.header)).map(col => col.header)
    if (missingHeaders.includes("Name")) {
        warnings.push('CSV is missing the required "Name" column. ' + "Imported rows will have no name and will fail validation.")
    }
    const optionalMissing = missingHeaders.filter(h => h !== "Name")
    if (optionalMissing.length > 0) {
        warnings.push(`CSV is missing columns that will be imported as blank: ${optionalMissing.join(", ")}.`)
    }
    const rows: Record<string, unknown>[] = result.data.map((csvRow, index) => {
        const row: Record<string, unknown> = {}
        const rowIndex = index + 1

        columns.forEach(col => {
            const csvValue = csvRow[col.header] ?? ""
            const warning = setImportValue(row, csvValue, col, referenceableData, rowIndex, currencyMultiplier)
            if (warning) {
                warnings.push(warning)
            }
        })

        return row
    })

    return { rows, warnings }
}
